/**
 * Notification Toast
 *
 * Popup notification for new real-time alerts.
 * Auto-dismisses after 5 seconds with a progress bar.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { cn } from "@/lib/utils";

const TOAST_DURATION = 5000;

const severityConfig = {
  info: {
    icon: Info,
    bgClass:
      "bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800/40",
    iconClass: "text-blue-500",
    iconBgClass: "bg-blue-100 dark:bg-blue-900/40",
    progressClass: "bg-blue-400",
  },
  success: {
    icon: CheckCircle,
    bgClass:
      "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800/40",
    iconClass: "text-emerald-500",
    iconBgClass: "bg-emerald-100 dark:bg-emerald-900/40",
    progressClass: "bg-emerald-400",
  },
  warning: {
    icon: AlertCircle,
    bgClass:
      "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800/40",
    iconClass: "text-amber-500",
    iconBgClass: "bg-amber-100 dark:bg-amber-900/40",
    progressClass: "bg-amber-400",
  },
  error: {
    icon: XCircle,
    bgClass:
      "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800/40",
    iconClass: "text-red-500",
    iconBgClass: "bg-red-100 dark:bg-red-900/40",
    progressClass: "bg-red-400",
  },
} as const;

export function NotificationToast() {
  const navigate = useNavigate();
  const pendingToast = useNotificationStore((s) => s.pendingToast);
  const clearPendingToast = useNotificationStore((s) => s.clearPendingToast);
  const openPanel = useNotificationStore((s) => s.openPanel);

  // Auto-dismiss
  useEffect(() => {
    if (!pendingToast) return;

    const timer = setTimeout(() => {
      clearPendingToast();
    }, TOAST_DURATION);

    return () => clearTimeout(timer);
  }, [pendingToast, clearPendingToast]);

  if (!pendingToast) return null;

  const {
    icon: Icon,
    bgClass,
    iconClass,
    iconBgClass,
    progressClass,
  } = severityConfig[pendingToast.severity];

  const handleClick = () => {
    if (pendingToast.actionUrl) {
      navigate(pendingToast.actionUrl);
    } else {
      openPanel();
    }
    clearPendingToast();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearPendingToast();
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50",
        "w-[380px] max-w-[calc(100vw-2rem)]",
        "animate-in slide-in-from-right-full fade-in-0",
        "duration-300",
      )}
      role="alert"
      aria-live="polite"
    >
      <div
        className={cn(
          "relative flex items-start gap-3 p-3.5 overflow-hidden",
          "border rounded-xl shadow-xl",
          "cursor-pointer transition-all duration-150",
          "hover:shadow-2xl hover:scale-[1.01]",
          bgClass,
        )}
        onClick={handleClick}
      >
        {/* Icon */}
        <div
          className={cn(
            "shrink-0 flex items-center justify-center",
            "h-9 w-9 rounded-lg",
            iconClass,
            iconBgClass,
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm font-semibold leading-tight">
            {pendingToast.title}
          </p>
          <p className="text-[13px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug">
            {pendingToast.message}
          </p>
          {pendingToast.actionLabel && (
            <p className="text-xs text-primary mt-1.5 font-medium">
              {pendingToast.actionLabel} &rarr;
            </p>
          )}
        </div>

        {/* Dismiss */}
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 h-6 w-6 -mt-0.5 -mr-0.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <X className="h-3.5 w-3.5" />
        </Button>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden bg-black/5 dark:bg-white/5">
          <div
            className={cn("h-full rounded-full", progressClass)}
            style={{
              animation: `notification-toast-shrink ${TOAST_DURATION}ms linear forwards`,
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes notification-toast-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
